import dayjs from 'dayjs';
import { uniqBy } from 'lodash-es';
import { pick } from 'lodash-es';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { DriverSchedulerStoreStorageKeys } from '~constants/dispatch';
import { JobAssignmentType } from '~constants/enums';
import { AccountTypeahead } from '~hooks/useAccount/models';
import { DriverBasic } from '~hooks/useDrivers/models';
import { Job } from '~hooks/useJob/models';
import { OrderBasic } from '~hooks/useOrders/models';
import { pendingJobStates } from '~pages/Dispatch/constants/jobStates';
import { Pagination } from '~services/pagination';
import { Nullable } from '~types/Nullable';

export const DEFAULT_PAGINATION_STATE: Pagination = { limit: 50, page: 1 };

type DateFilters = {
  startDate?: string;
  endDate?: string;
};

export type JobsFilters = {
  customers?: string[];
  dispatchNumberIds?: string[];
  dropOffSites?: string[];
  pickUpSites?: string[];
  projects?: string[];
  projectsExternalIds?: string[];
  search?: string;
};
export type AssigneesFilters = {
  assigneeType: JobAssignmentType;
  search?: string;
};

export type UnassignedOrderGroup = {
  order: OrderBasic;
  jobs: Job[];
};

class DriverSchedulerStore {
  assignedJobs: Job[] = [];
  assignedJobsFilters: JobsFilters = {};
  assignees: (DriverBasic | AccountTypeahead)[] = [];
  assigneesFilters: AssigneesFilters = {
    assigneeType: JobAssignmentType.DRIVER,
  };
  assigneesPagination: Pagination = DEFAULT_PAGINATION_STATE;
  dateFilters: DateFilters = {};
  draggedJob: Nullable<Job> = null;
  filteredAssignees: (DriverBasic | AccountTypeahead)[] = [];
  hasMoreAssignees: boolean = true;
  isLoadingAssignedJobs: boolean = false;
  isLoadingAssignees: boolean = false;
  isFetchingMoreAssignees: boolean = false;
  isUpdatingJob: boolean = false;
  jobAssignments: Record<string, Nullable<Job>> = {};
  jobStacks: Record<string, Job[]> = {};
  unassignedJobs: Job[] = [];
  unassignedJobsFilters: JobsFilters = {};
  unassignedJobsPagination: Pagination = DEFAULT_PAGINATION_STATE;
  unassignedJobsRequestsInFlight = 0;
  isUnassignedJobsFullyLoaded = false;
  isEagerLoadingUnassignedJobs = false;
  // True while the Orders sub-tab is mounted. Lets DriversViewEffects defer the
  // unassigned-jobs load to the Orders eager loader so the two never run at once.
  isOrdersViewActive = false;
  // Bumped on each eager load so a superseded in-flight load can detect it is
  // stale and stop writing (e.g. when filters change mid-load). Not observable —
  // it only coordinates async control flow.
  unassignedLoadGeneration = 0;

  constructor() {
    makeObservable(this, {
      assignedJobs: observable,
      assignedJobsFetchStart: action,
      assignedJobsFilters: observable,
      assignees: observable,
      assigneesFetchEnd: action,
      assigneesFetchStart: action,
      assigneesFilters: observable,
      assigneesPagination: observable,
      clearAssignees: action,
      clearUnassignedJobs: action,
      dateFilters: observable,
      draggedJob: observable,
      fetchAssignedJobsEnd: action,
      fetchUnassignedJobsEnd: action,
      filteredAssignees: observable,
      hasMoreAssignees: observable,
      isEagerLoadingUnassignedJobs: observable,
      isLoadingAssignedJobs: observable,
      isLoadingAssignees: observable,
      isLoadingUnassignedJobs: computed,
      isFetchingMoreAssignees: observable,
      isOrdersViewActive: observable,
      isUnassignedJobsFullyLoaded: observable,
      isUpdatingJob: observable,
      jobAssignments: observable,
      jobStacks: observable,
      setAssignedJobsFilters: action,
      setAssigneesFilters: action,
      setDateFilters: action,
      setDraggedJob: action,
      setDraggedOrder: action,
      setEagerLoadingUnassignedJobs: action,
      setFilteredAssignees: action,
      abandonEagerUnassignedLoad: action,
      setOrdersViewActive: action,
      startEagerUnassignedLoad: action,
      setUnassignedJobsFullyLoaded: action,
      promoteJobInStack: action,
      setJobAssignments: action,
      setUnassignedJobsFilters: action,
      unassignedJobs: observable,
      unassignedJobsFetchAbandoned: action,
      unassignedJobsFetchStart: action,
      unassignedJobsFilters: observable,
      unassignedJobsPagination: observable,
      unassignedJobsRequestsInFlight: observable,
      unassignedOrderGroups: computed,
      updateJob: action,
      updateJobEnd: action,
      updateJobStart: action,
    });
  }

  get unassignedOrderGroups() {
    const groups = new Map<string, UnassignedOrderGroup>();

    for (const job of this.unassignedJobs) {
      const orderId = job.order.id;
      const existing = groups.get(orderId);
      if (existing) {
        existing.jobs.push(job);
      } else {
        groups.set(orderId, { order: job.order, jobs: [job] });
      }
    }

    const orderGroups = Array.from(groups.values());

    // Sort each order's jobs by start time so the first job is the one that
    // setDraggedOrder assigns — the OrderCard preview and the drop must agree.
    for (const group of orderGroups) {
      group.jobs.sort(
        (a, b) => (a.jobStartAt?.valueOf() ?? 0) - (b.jobStartAt?.valueOf() ?? 0),
      );
    }

    return orderGroups.sort((a, b) => {
      const aLabel = a.order.dispatchNumber?.name ?? a.order.orderId ?? a.order.id;
      const bLabel = b.order.dispatchNumber?.name ?? b.order.orderId ?? b.order.id;
      return aLabel.localeCompare(bLabel);
    });
  }

  // The Orders eager loader and the Jobs list fetch through separate hook
  // instances, so their requests overlap. Counting them keeps the spinner up
  // until the last one settles rather than the first, and flooring at zero stops
  // an unpaired settle from stranding the overlay.
  get isLoadingUnassignedJobs() {
    return this.unassignedJobsRequestsInFlight > 0;
  }

  unassignedJobsFetchAbandoned() {
    runInAction(() => {
      this.unassignedJobsRequestsInFlight = Math.max(
        0,
        this.unassignedJobsRequestsInFlight - 1,
      );
    });
  }

  unassignedJobsFetchStart() {
    runInAction(() => {
      this.unassignedJobsRequestsInFlight += 1;
    });
  }

  assignedJobsFetchStart() {
    runInAction(() => {
      this.isLoadingAssignedJobs = true;
    });
  }

  assigneesFetchStart(loadMore?: boolean) {
    runInAction(() => {
      if (loadMore) {
        this.isFetchingMoreAssignees = true;
      } else {
        this.isLoadingAssignees = true;
      }
    });
  }

  updateJobStart() {
    runInAction(() => {
      this.isUpdatingJob = true;
    });
  }

  updateJobEnd() {
    runInAction(() => {
      this.isUpdatingJob = false;
    });
  }

  fetchUnassignedJobsEnd(jobs: Job[], pagination?: Pagination, companyId?: string) {
    runInAction(() => {
      this.unassignedJobsRequestsInFlight = Math.max(
        0,
        this.unassignedJobsRequestsInFlight - 1,
      );
      const filteredJobs = jobs.filter((job) => {
        const vendorCompanyIsCurrentCompany =
          job.vendorJobAssignment?.vendorAccount?.id === companyId;
        return (
          !job.driver ||
          (!job.driver && job.vendorJobAssignment && vendorCompanyIsCurrentCompany)
        );
      });

      this.unassignedJobs = uniqBy(
        [...this.unassignedJobs, ...filteredJobs],
        (job) => job.id,
      );

      this.unassignedJobsPagination = {
        limit: DEFAULT_PAGINATION_STATE.limit,
        after: pagination?.after ?? '',
        page: DEFAULT_PAGINATION_STATE.page,
      };
    });
  }

  fetchAssignedJobsEnd(jobs: Job[]) {
    runInAction(() => {
      this.isLoadingAssignedJobs = false;
      this.assignedJobs = uniqBy([...this.assignedJobs, ...jobs], (job) => job.id);
    });
  }

  assigneesFetchEnd(
    assignees: (DriverBasic | AccountTypeahead)[],
    pagination?: Pagination,
    loadMore?: boolean,
  ) {
    runInAction(() => {
      if (loadMore) {
        this.isFetchingMoreAssignees = false;
        this.hasMoreAssignees = !!pagination?.after;

        // Append to existing assignees
        this.assignees = uniqBy(
          [...this.assignees, ...assignees],
          (assignee) => assignee.id,
        );
        this.filteredAssignees = this.assignees;

        // Update pagination cursor
        this.assigneesPagination = {
          ...this.assigneesPagination,
          after: pagination?.after ?? '',
        };
      } else {
        this.isLoadingAssignees = false;
        this.assignees = uniqBy(
          this.assignees.concat(assignees),
          (assignee) => assignee.id,
        );
        this.filteredAssignees = this.assignees;
        this.hasMoreAssignees = !!pagination?.after;

        this.assigneesPagination = {
          limit: DEFAULT_PAGINATION_STATE.limit,
          after: pagination?.after ?? '',
          page: DEFAULT_PAGINATION_STATE.page,
        };
      }
    });
  }

  addUnassignedJob(job: Job) {
    runInAction(() => {
      this.unassignedJobs = uniqBy([...this.unassignedJobs, job], (job) => job.id);
    });
  }

  removeUnassignedJob(job: Job) {
    runInAction(() => {
      const unassignedIndex = this.unassignedJobs.findIndex((j) => j.id === job.id);

      if (unassignedIndex > -1) {
        this.unassignedJobs.splice(unassignedIndex, 1);
      }
    });
  }

  updateJob(job: Job) {
    if (!job) return;

    const unassignedIndex = this.unassignedJobs.findIndex((j) => j.id === job.id);
    const assignedIndex = this.assignedJobs.findIndex((j) => j.id === job.id);

    runInAction(() => {
      const isPending = pendingJobStates.includes(job.status);
      const isUnassigned = !job.driver && !job.vendorJobAssignment;

      if (unassignedIndex > -1) {
        this.unassignedJobs.splice(unassignedIndex, 1, job);
      } else if (assignedIndex > -1) {
        this.assignedJobs.splice(assignedIndex, 1, job);
      }

      // Keep jobStacks in sync so the StackedJobsMenu shows fresh data
      for (const slotKey of Object.keys(this.jobStacks)) {
        const stack = this.jobStacks[slotKey];
        const stackIndex = stack.findIndex((j) => j.id === job.id);
        if (stackIndex > -1) {
          stack.splice(stackIndex, 1, job);
          break;
        }
      }

      if (
        unassignedIndex === -1 &&
        assignedIndex === -1 &&
        this.dateFilters.startDate &&
        this.dateFilters.endDate &&
        isUnassigned &&
        isPending
      ) {
        const jobStartDate = dayjs.tz(job.jobStartAt);
        const selectedStartDate = dayjs.tz(this.dateFilters.startDate).startOf('day');
        const selectedEndDate = dayjs.tz(this.dateFilters.endDate).endOf('day');
        const matchesCurrentlySelectedDateRange = jobStartDate.isBetween(
          selectedStartDate,
          selectedEndDate,
          'seconds',
          '[]',
        );

        if (matchesCurrentlySelectedDateRange) {
          this.unassignedJobs.push(job);
        }
      }
    });
  }

  promoteJobInStack(slotKey: string, jobId: string) {
    const stack = this.jobStacks[slotKey];
    if (!stack) return;

    const jobIndex = stack.findIndex((j) => j.id === jobId);
    if (jobIndex <= -1) return;

    runInAction(() => {
      // Move job to end of array so it renders last (on top in DOM)
      const [job] = stack.splice(jobIndex, 1);
      stack.push(job);
      this.jobAssignments[slotKey] = job;
    });
  }

  setJobAssignments(
    newAssignments: Record<string, Nullable<Job>>,
    newStacks?: Record<string, Job[]>,
  ) {
    runInAction(() => {
      this.jobAssignments = newAssignments;
      if (newStacks !== undefined) {
        this.jobStacks = newStacks;
      }
    });
  }

  setUnassignedJobsFilters(filters: JobsFilters, merge?: boolean) {
    runInAction(() => {
      this.unassignedJobsFilters = merge
        ? { ...this.unassignedJobsFilters, ...filters }
        : filters;
      this.isUnassignedJobsFullyLoaded = false;
    });
  }

  retrieveAssignedJobsStoredFilters() {
    const storedFilters = localStorage.getItem(
      DriverSchedulerStoreStorageKeys.APPLIED_ASSIGNED_JOBS_FILTERS,
    );
    const parsedFilters = (storedFilters ? JSON.parse(storedFilters) : {}) as JobsFilters;

    return parsedFilters;
  }

  restoreAssignedJobsFilters() {
    runInAction(() => {
      this.assignedJobsFilters = {
        ...this.assignedJobsFilters,
        ...this.retrieveAssignedJobsStoredFilters(),
      };
    });
  }

  persistAssignedJobsFilters(filters: JobsFilters) {
    runInAction(() => {
      const toPersist: Array<keyof JobsFilters> = [
        'customers',
        'dispatchNumberIds',
        'dropOffSites',
        'pickUpSites',
        'projects',
        'projectsExternalIds',
      ];

      const shouldPersistFilters = Object.keys(filters).some((key) => {
        return toPersist.includes(key as keyof JobsFilters);
      });

      if (!shouldPersistFilters) {
        return;
      }

      const values = pick(filters, toPersist);
      localStorage.setItem(
        DriverSchedulerStoreStorageKeys.APPLIED_ASSIGNED_JOBS_FILTERS,
        JSON.stringify(values),
      );
    });
  }

  setAssignedJobsFilters(filters: JobsFilters, merge?: boolean) {
    const newFilters = merge ? { ...this.assignedJobsFilters, ...filters } : filters;

    if (filters.search) {
      this.resetAssignedJobs({ search: filters.search });
    } else {
      const previousFilters = this.retrieveAssignedJobsStoredFilters();

      this.resetAssignedJobs({
        ...previousFilters,
        ...newFilters,
        search: '',
      });
    }

    this.persistAssignedJobsFilters(newFilters);
  }

  resetAssignedJobs(assignedJobsFilters: JobsFilters = {}) {
    runInAction(() => {
      this.assignedJobs = [];
      this.isLoadingAssignedJobs = false;
      this.assignedJobsFilters = assignedJobsFilters;
      this.jobAssignments = {};
      this.jobStacks = {};
    });
  }

  setAssigneesFilters(filters: Partial<AssigneesFilters>) {
    runInAction(() => {
      this.assigneesFilters = { ...this.assigneesFilters, ...filters };
    });
  }

  setFilteredAssignees(assignees: (DriverBasic | AccountTypeahead)[]) {
    runInAction(() => {
      this.filteredAssignees = assignees;
    });
  }

  clearAssignees() {
    runInAction(() => {
      this.assignees = [];
      this.assigneesPagination = DEFAULT_PAGINATION_STATE;
      this.hasMoreAssignees = true;
      this.isFetchingMoreAssignees = false;
    });
  }

  clearUnassignedJobs() {
    runInAction(() => {
      this.unassignedJobs = [];
      this.unassignedJobsPagination = DEFAULT_PAGINATION_STATE;
      this.isUnassignedJobsFullyLoaded = false;
      this.isEagerLoadingUnassignedJobs = false;
      // Supersede any in-flight eager load so it stops appending to the list we
      // just cleared (e.g. when switching from the Orders sub-tab to Jobs).
      this.unassignedLoadGeneration += 1;
    });
  }

  setUnassignedJobsFullyLoaded(value: boolean) {
    runInAction(() => {
      this.isUnassignedJobsFullyLoaded = value;
    });
  }

  setEagerLoadingUnassignedJobs(value: boolean) {
    runInAction(() => {
      this.isEagerLoadingUnassignedJobs = value;
    });
  }

  setOrdersViewActive(value: boolean) {
    runInAction(() => {
      this.isOrdersViewActive = value;
    });
  }

  // Begins a fresh eager load: clears the current set, marks loading, and bumps
  // the generation. Returns the new generation so the caller can detect if a
  // later load has superseded it and stop writing stale results.
  startEagerUnassignedLoad() {
    runInAction(() => {
      this.unassignedLoadGeneration += 1;
      this.unassignedJobs = [];
      this.unassignedJobsPagination = DEFAULT_PAGINATION_STATE;
      this.isUnassignedJobsFullyLoaded = false;
      this.isEagerLoadingUnassignedJobs = true;
    });

    return this.unassignedLoadGeneration;
  }

  // Supersedes an in-flight eager load without touching the collection, so the
  // Orders sub-tab can stop paging when it unmounts and leave whatever is loaded
  // for the Jobs list to clear on its own terms.
  abandonEagerUnassignedLoad() {
    runInAction(() => {
      this.unassignedLoadGeneration += 1;
      this.isEagerLoadingUnassignedJobs = false;
      this.isUnassignedJobsFullyLoaded = false;
    });
  }

  setDateFilters(filters: DateFilters) {
    runInAction(() => {
      this.dateFilters = filters;
      this.isUnassignedJobsFullyLoaded = false;
      this.resetAssignedJobs(this.assignedJobsFilters);
    });
  }

  setDraggedJob(job: Nullable<Job>) {
    runInAction(() => {
      this.draggedJob = job;
    });
  }

  setDraggedOrder(orderId: Nullable<string>) {
    runInAction(() => {
      if (!orderId) {
        this.draggedJob = null;
        return;
      }

      // unassignedOrderGroups already sorts each order's jobs by start time, so
      // the first job is the next one to assign.
      const group = this.unassignedOrderGroups.find((g) => g.order.id === orderId);
      this.draggedJob = group?.jobs[0] ?? null;
    });
  }
}

export default DriverSchedulerStore;
